import * as z from 'zod';

/** Job name used by the QA campaign funnel orchestration job. */
export const QA_CAMPAIGN_FUNNEL_JOB_NAME = 'qa_campaign_funnel';

// Enums
export const qaCampaignFunnelStepEnum = z.enum([
  'delete_conflicting_campaigns',
  'import_buyers',
  'landing_page_analysis',
  'enable_all_blocks',
  'create_audience_and_lead',
  'attach_audience',
  'validate_audience',
  'generate_pages',
  'confirm_steps',
  'publish',
  'done',
]);
export type QaCampaignFunnelStep = z.infer<typeof qaCampaignFunnelStepEnum>;

export const qaCampaignFunnelErrorCodeEnum = z.enum([
  'audience_conflict',
  'no_blocks_identified',
  'page_generation_failed',
  'page_generation_timeout',
  'buyer_import_failed',
  'lead_import_failed',
  'no_generation_started',
  'publish_failed',
  'publish_timeout',
  'qa_funnel_error',
  'interrupted',
]);
export type QaCampaignFunnelErrorCode = z.infer<
  typeof qaCampaignFunnelErrorCodeEnum
>;

// Shape of the funnel job's `state` payload (`ReadJob.state` is untyped —
// parse it with `parseQaCampaignFunnelState`). `campaign_id` / `audience_id` /
// `campaign_status` only appear once the run has reached the matching step.
export const qaCampaignFunnelStateSchema = z.object({
  step: qaCampaignFunnelStepEnum.nullish().catch(null),
  campaignId: z.uuid().nullish().catch(null),
  audienceId: z.uuid().nullish().catch(null),
  campaignStatus: z.string().nullish().catch(null),
});
export type QaCampaignFunnelState = z.infer<typeof qaCampaignFunnelStateSchema>;

/** Safely read a job's `state` as a QA campaign funnel state. */
export const parseQaCampaignFunnelState = (
  state: unknown,
): QaCampaignFunnelState | null => {
  const result = qaCampaignFunnelStateSchema.safeParse(state);
  return result.success ? result.data : null;
};

// Create schema — payload of POST /api-tools/v1/qa/organizations/{id}/qa-campaign
export const startQaCampaignFunnelSchema = z.object({
  campaignUrl: z
    .string()
    .min(1, 'Landing page URL is required')
    .url('Landing page URL must be a valid URL'),
  leadCompanyDomain: z.string().optional(),
  force: z.boolean().optional(),
});
export type StartQaCampaignFunnel = z.infer<typeof startQaCampaignFunnelSchema>;

// Read schema — immediate response of the start call
export const readQaCampaignFunnelRunSchema = z.object({
  campaignId: z.uuid().nullable(),
  jobId: z.uuid().nullable(),
});
export type ReadQaCampaignFunnelRun = z.infer<
  typeof readQaCampaignFunnelRunSchema
>;
